import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";
import { GREEN_LINE } from "apps/project-iq/utils/diagnosticUtils";

const TAB_CONTENT_HEIGHT = "500px";

export const useStyles = makeStyles({
  wideDialogSurface: {
    minWidth: "680px",
    width: "680px",
  },
  dialogContent: {
    minHeight: TAB_CONTENT_HEIGHT,
    maxHeight: "70vh",
    overflow: "hidden",
    position: "relative",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },

  inheritedSection: {
    marginTop: designTokens.spacing[2],
    padding: designTokens.spacing[3],
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.bg2,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },
  inheritedHeader: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },
  inheritedLabel: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    margin: "0",
  },
  inheritedHint: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    margin: "0",
    lineHeight: "1.4",
  },
  inheritedGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: designTokens.spacing[3],
  },

  fieldLabelRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
  },
  prefillBadge: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "10px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.tealInk,
    backgroundColor: projectIqTokens.tealSoft,
    border: `1px solid ${projectIqTokens.tealLine}`,
    borderRadius: designTokens.borderRadius.full,
    paddingTop: "1px",
    paddingBottom: "1px",
    paddingLeft: designTokens.spacing[2],
    paddingRight: designTokens.spacing[2],
    lineHeight: "1.4",
    letterSpacing: "0.02em",
    textTransform: "uppercase" as const,
  },

  infoBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: designTokens.spacing[2],
    padding: designTokens.spacing[3],
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.tealSoft,
    border: `1px solid ${projectIqTokens.tealLine}`,
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.tealInk,
    lineHeight: "1.5",
  },
  infoBannerIcon: {
    flexShrink: 0,
    marginTop: "1px",
    color: projectIqTokens.teal,
    fontSize: "14px",
  },

  attachmentList: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },
  confirmedCard: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    borderRadius: designTokens.borderRadius.md,
    border: `1px solid ${designTokens.accent[200]}`,
    backgroundColor: designTokens.accent[50],
    minWidth: 0,
  },
  confirmedCardInfo: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },
  confirmedFileName: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.neutral[900],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  confirmedFileDetails: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
  },

  pendingCard: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
    padding: designTokens.spacing[3],
    borderRadius: designTokens.borderRadius.md,
    border: `1px solid ${designTokens.neutral[200]}`,
    backgroundColor: projectIqTokens.card,
  },
  pendingCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: designTokens.spacing[2],
  },
  pendingCardFileInfo: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    minWidth: 0,
    flex: 1,
  },
  pendingFileName: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: 1,
  },
  pendingFileSize: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  pendingCardActions: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    flexShrink: 0,
  },

  fileTypeBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: `2px ${designTokens.spacing[2]}`,
    borderRadius: designTokens.borderRadius.sm,
    fontSize: "10px",
    fontWeight: designTokens.fontWeight.bold,
    flexShrink: 0,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
  },
  fileTypeBadgeCsv: {
    backgroundColor: projectIqTokens.tealSoft,
    color: projectIqTokens.tealInk,
    border: `1px solid ${projectIqTokens.tealLine}`,
  },
  fileTypeBadgeXls: {
    backgroundColor: projectIqTokens.greenBg,
    color: projectIqTokens.green,
    border: `1px solid ${GREEN_LINE}`,
  },

  workspaceFileLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: designTokens.spacing[8],
  },

  fieldsRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: designTokens.spacing[3],
    alignItems: "flex-start",
  },
  fieldSelect: {
    minWidth: "120px",
  },
  fieldSelectWide: {
    minWidth: "160px",
  },
});
